import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, X, Upload } from 'lucide-react';
import api from '../../utils/api';

const NewsManagement = ({ onUpdate }) => {
  const [news, setNews] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    excerpt: '',
    image_url: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await api.get('/news');
      setNews(response.data);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('ニュースの取得に失敗しました:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'エラー',
        description: 'ファイルサイズは2MB以下にしてください',
        variant: 'destructive',
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'エラー',
        description: '画像ファイルを選択してください',
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setImagePreview(base64String);
      setFormData({ ...formData, image_url: base64String });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData({ ...formData, image_url: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingNews) {
        await api.put(`/news/${editingNews.id}`, formData);
        toast({
          title: '更新完了',
          description: 'ニュースを更新しました',
        });
      } else {
        await api.post('/news', formData);
        toast({
          title: '作成完了',
          description: '新しいニュースを作成しました',
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchNews();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '操作に失敗しました',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingNews(item);
    setFormData({
      title: item.title,
      content: item.content,
      category: item.category,
      excerpt: item.excerpt,
      image_url: item.image_url || '',
    });
    setImagePreview(item.image_url || null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) {
      return;
    }

    try {
      await api.delete(`/news/${id}`);
      toast({
        title: '削除完了',
        description: 'ニュースを削除しました',
      });
      fetchNews();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '削除に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const handleTogglePublish = async (item) => {
    try {
      const response = await api.patch(`/news/${item.id}/toggle-publish`);
      const newStatus = response.data.is_published;
      
      toast({
        title: newStatus ? '公開しました' : '非公開にしました',
        description: `「${item.title}」を${newStatus ? '公開' : '非公開'}にしました`,
      });
      fetchNews();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '公開状態の変更に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: '',
      excerpt: '',
      image_url: '',
    });
    setImagePreview(null);
    setEditingNews(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  return (
    <Card className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border-amber-500/30 backdrop-blur-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <CardTitle className="text-xl sm:text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
            ニュース管理
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            {!isEditMode && (
              <Button 
                onClick={() => setIsEditMode(true)}
                variant="outline"
                className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
              >
                <Edit className="w-4 h-4 mr-2" />
                編集モード
              </Button>
            )}
            {isEditMode && (
              <Button 
                onClick={() => setIsEditMode(false)}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <X className="w-4 h-4 mr-2" />
                編集終了
              </Button>
            )}
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/30"
              >
                <Plus className="w-4 h-4 mr-2" />
                新規作成
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 border-amber-500/30 text-gray-900">
              <DialogHeader>
                <DialogTitle className="text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                  {editingNews ? 'ニュースを編集' : '新しいニュースを作成'}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  {editingNews ? 'ニュースの内容を編集します' : '新しいニュースを作成します'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* 画像アップロード */}
                <div className="space-y-2">
                  <Label className="text-gray-600">ニュース画像</Label>
                  <div className="flex items-center space-x-4">
                    {imagePreview && (
                      <div className="relative w-32 h-32 border-2 border-amber-500/30 rounded-lg overflow-hidden bg-white/10">
                        <img
                          src={imagePreview}
                          alt="News"
                          className="w-full h-full object-cover"
                          data-testid="news-image-preview"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          data-testid="news-image-remove-btn"
                          className="absolute top-1 right-1 bg-red-500/80 hover:bg-red-600 text-gray-900 rounded-full p-1"
                          title="画像を削除"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <div className="flex-1">
                      <Label
                        htmlFor="news-image-upload"
                        className="cursor-pointer inline-flex items-center px-4 py-2 bg-white/50 border border-amber-500/30 rounded-lg hover:bg-amber-500/10 transition-all"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        画像をアップロード
                      </Label>
                      <Input
                        id="news-image-upload"
                        data-testid="news-image-upload-input"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        推奨: PNG、JPG形式、最大2MB
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title" className="text-gray-600">タイトル</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category" className="text-gray-600">カテゴリ</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="例: お知らせ、採用情報"
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="excerpt" className="text-gray-600">要約</Label>
                  <Textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    rows={2}
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content" className="text-gray-600">本文</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={8}
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleDialogClose}
                    className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                  >
                    キャンセル
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                    disabled={loading}
                  >
                    {loading ? '処理中...' : (editingNews ? '更新' : '作成')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {news.map((item) => (
            <div
              key={item.id}
              className="border border-amber-500/20 rounded-lg p-4 hover:border-amber-500/50 bg-white/30 transition-all backdrop-blur-sm"
            >
              <div className="flex justify-between items-start gap-4">
                {item.image_url && (
                  <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden border border-amber-500/30">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      data-testid={`news-thumbnail-${item.id}`}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xs bg-amber-500/20 text-amber-500 px-2 py-1 rounded border border-amber-500/30">
                      {item.category}
                    </span>
                    <span className="text-xs text-gray-500">{item.date}</span>
                    {/* 公開状態バッジ */}
                    {item.is_published ? (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded border border-green-500/30">
                        公開中
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-100/20 text-gray-600 px-2 py-1 rounded border border-gray-300/30">
                        非公開
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.excerpt}</p>
                </div>
                <div className="flex space-x-2 ml-4">
                  {/* 公開/非公開トグルボタン（常に表示） */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleTogglePublish(item)}
                    className={item.is_published 
                      ? "border-green-500/30 text-green-400 hover:bg-green-500/10" 
                      : "border-gray-300/30 text-gray-600 hover:bg-gray-100/10"}
                    title={item.is_published ? "非公開にする" : "公開する"}
                  >
                    {item.is_published ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    )}
                  </Button>
                  {isEditMode && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEdit(item)}
                        className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleDelete(item.id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default NewsManagement;
